import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, Save, X, User, UserCheck, UserX } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const AdminUsersPage = () => {
    const [users, setUsers] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        role: 'setup',
        role_ids: [],
        is_active: true
    });
    const [error, setError] = useState('');

    useEffect(() => {
        loadUsers();
        loadRoles();
    }, []);

    const loadRoles = async () => {
        try {
            const response = await fetch(`${API_URL}/roles`, {
                headers: getAuthHeaders()
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableRoles(data.roles || []);
            }
        } catch (error) {
            console.error('Error loading roles:', error);
        }
    };

    const getAuthHeaders = () => {
        const token = localStorage.getItem('accessToken');
        return {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
        };
    };

    const loadUsers = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/users`, {
                headers: getAuthHeaders()
            });
            if (response.ok) {
                const data = await response.json();
                setUsers(data.users || []);
            } else {
                throw new Error('Failed to fetch users');
            }
        } catch (error) {
            console.error('Error loading users:', error);
            setError('Failed to load users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = () => {
        setEditingId(null);
        setFormData({
            email: '',
            first_name: '',
            last_name: '',
            role: 'setup',
            role_ids: [],
            is_active: true
        });
        setError('');
        setIsModalOpen(true);
    };

    const handleEditUser = (user) => {
        setEditingId(user.id);
        const assignedRoleIds = (user.assigned_roles || []).map(r => r.id);
        setFormData({
            email: user.email,
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            role: user.role,
            role_ids: assignedRoleIds,
            is_active: user.is_active
        });
        setError('');
        setIsModalOpen(true);
    };

    const handleSaveUser = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.role_ids || formData.role_ids.length === 0) {
            setError('Please select at least one role');
            return;
        }

        setSaving(true);

        try {
            const url = editingId ? `${API_URL}/users/${editingId}` : `${API_URL}/users`;
            const role = editingId ? formData.role : 'setup';
            if (role === 'client') {
                setError('Client users are managed in Client Portal users. Use that page to add or edit client accounts.');
                setSaving(false);
                return;
            }

            const dataToSend = {
                email: formData.email,
                first_name: formData.first_name,
                last_name: formData.last_name,
                role,
                role_ids: formData.role_ids || [],
                is_active: formData.is_active
            };

            const response = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(dataToSend)
            });

            if (response.ok) {
                await loadUsers();
                setIsModalOpen(false);
                setEditingId(null);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to save user');
            }
        } catch (error) {
            console.error('Error saving user:', error);
            setError(error.message || 'Failed to save user');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteUser = async (user) => {
        if (!window.confirm(`Are you sure you want to delete user "${user.email}"?`)) {
            return;
        }

        try {
            setSaving(true);
            const response = await fetch(`${API_URL}/users/${user.id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (response.ok) {
                await loadUsers();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete user');
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            alert(`Failed to delete user: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingId(null);
        setFormData({
            email: '',
            first_name: '',
            last_name: '',
            role: 'setup',
            role_ids: [],
            is_active: true
        });
        setError('');
    };

    const handleRoleToggle = (roleId) => {
        const currentRoleIds = formData.role_ids || [];
        if (currentRoleIds.includes(roleId)) {
            setFormData({
                ...formData,
                role_ids: currentRoleIds.filter(id => id !== roleId)
            });
        } else {
            setFormData({
                ...formData,
                role_ids: [...currentRoleIds, roleId]
            });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const internalUsers = (users || []).filter((u) => u.role !== 'client');

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-xl">Loading users...</div>
            </div>
        );
    }

    return (
        <div>
            <div className="mb-6 flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Internal users</h2>
                    <p className="text-gray-600 mt-1">Manage your team: marketing delivery and internal users who have access to the full system (onboarding, workflows, event management, job list, etc.).</p>
                </div>
                <button
                    onClick={handleAddUser}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus size={20} />
                    Add User
                </button>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Role
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Last Login
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {internalUsers.length === 0 ? (
                                <tr>
                                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                    No internal users found. Add team members who need access to the full system.
                                </td>
                            </tr>
                        ) : (
                            internalUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{user.email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {user.first_name || user.last_name
                                                ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                                                : '-'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-wrap gap-1">
                                            {user.assigned_roles && user.assigned_roles.length > 0 ? (
                                                user.assigned_roles.map((role) => (
                                                    <span
                                                        key={role.id}
                                                        className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800"
                                                    >
                                                        {role.display_name || role.name}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                    user.role === 'admin' 
                                                        ? 'bg-blue-100 text-blue-800' 
                                                        : 'bg-green-100 text-green-800'
                                                }`}>
                                                    {user.role}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                            user.is_active 
                                                ? 'bg-green-100 text-green-800' 
                                                : 'bg-red-100 text-red-800'
                                        }`}>
                                            {user.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatDate(user.last_login_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={() => handleEditUser(user)}
                                            className="text-blue-600 hover:text-blue-900 mr-4"
                                        >
                                            <Edit size={18} className="inline" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteUser(user)}
                                            className="text-red-600 hover:text-red-900"
                                        >
                                            <Trash2 size={18} className="inline" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Add/Edit User Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
                        <div className="p-6 flex flex-col min-h-0 flex-1 overflow-hidden">
                            <h2 className="text-2xl font-bold mb-4">
                                {editingId ? 'Edit User' : 'Add New User'}
                            </h2>

                            {error && (
                                <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg shrink-0">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSaveUser} className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="user@example.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        First Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.first_name}
                                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="John"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.last_name}
                                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Doe"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Roles *
                                    </label>
                                    <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                                        {availableRoles.length === 0 ? (
                                            <p className="text-sm text-gray-500">Loading roles...</p>
                                        ) : (
                                            availableRoles.map((role) => (
                                                <label
                                                    key={role.id}
                                                    className="flex items-center gap-2 py-2 hover:bg-gray-50 px-2 rounded cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={(formData.role_ids || []).includes(role.id)}
                                                        onChange={() => handleRoleToggle(role.id)}
                                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm text-gray-700">
                                                        {role.display_name || role.name}
                                                    </span>
                                                    {role.description && (
                                                        <span className="text-xs text-gray-500 ml-auto">
                                                            {role.description}
                                                        </span>
                                                    )}
                                                </label>
                                            ))
                                        )}
                                    </div>
                                    {(formData.role_ids || []).length === 0 && (
                                        <p className="text-xs text-red-500 mt-1">Please select at least one role</p>
                                    )}
                                </div>

                                {editingId && (
                                    <div>
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_active}
                                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                                className="rounded border-gray-300"
                                            />
                                            <span className="text-sm font-medium text-gray-700">Active</span>
                                        </label>
                                    </div>
                                )}

                                <div className="flex justify-end gap-4 mt-6">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : editingId ? 'Update User' : 'Create User'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUsersPage;

